/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { sanitizeHtml, isSafeRedirectUrl } from "../utils/sanitize";
import styled from "styled-components";
import { usePaymentProcessors } from "../lib/react-query/usePaymentProcessors";
import Container from "../components/Container";
import { useInitPayment } from "../lib/react-query/useInitPayment";
import LoadingIndicator from "../components/LoadingIndicator";
import Title from "../components/ui/Title";
import PaymentProcessorItem from "../components/booking/PaymentProcessorItem";
import PaymentProcessorsList from "../components/booking/PaymentProcessorsList";
import Button, { buttonBaseStyles, buttonSizeStyles, buttonVariantStyles } from "../components/ui/Button";
import usePluginUtils from "../hooks/usePluginUtils";
import PromoCodeInput from "../components/booking/checkout/PromoCodeInput";
import TipSelection from "../components/booking/checkout/TipSelection";
import CheckoutSummary from "../components/booking/checkout/CheckoutSummary";
import Note from "../components/ui/Note";
import { useInvoicePayFullAmount } from "../lib/react-query/useInvoicePayFullAmount";
import { usePackageInstances } from "../lib/react-query/usePackages";
import PackageInstanceSection from "../components/booking/checkout/PackageInstanceSection";
import PackageInstanceItem from "../components/booking/checkout/PackageInstanceItem";
import { useInvoicePayDepositAmount } from "../lib/react-query/useInvoicePayDepositAmount";
import { usePaymentSelection } from "../hooks/checkout/usePaymentSelection";
import SavePaymentMethodOption from "../components/payment/SavePaymentMethodOption";
import SavedPaymentMethodsList from "../components/payment/SavedPaymentMethodsList";
import { usePackageSelection } from "../hooks/checkout/usePackageSelection";
import { useRecurringInvoicePayment } from "../hooks/checkout/useRecurringInvoicePayment";
import { useConfig } from "../lib/react-query/useConfig";
import { useAssetsSrc } from "../hooks/useAssetsSrc";
import { CART_LINE_TYPES, PAYMENT_PROCESSORS_ICONS_PATH, TOAST_ERROR_MESSAGE } from "../constants/constants";
import useInvoice from "../hooks/checkout/useInvoice";
import Text from "../components/ui/Text";
import Stack from "../components/ui/Stack";
import { useNavigate } from "react-router-dom";
import { useActiveFlow } from "../hooks/useActiveFlow";
import { getInvoiceStatusRoute } from "../utils/helpers/invoice";
import { FlowType } from "../store/slices/flowSlice";
import {
  buildCallbackUrl,
  buildRelayUrl,
  buildRelayOrigin,
  isWidgetFramed,
} from "../hooks/checkout/buildCallbackUrl";
import usePaymentPopup from "../hooks/checkout/usePaymentPopup";
import useNavigateToPaymentStatus from "../hooks/checkout/useNavigateToPaymentStatus";
import { getMembershipCallback, clearMembershipCallback } from "../utils/membershipCallback";
import RecurringChargesBlock from "../components/membership/RecurringChargesBlock";
import { useToast } from "../components/ui/Toast";
import { extractErrorMessage } from "../utils/api/apiErrors";

const StyledTitle = styled(Title)`
  && {
    margin-bottom: 1rem;
  }
`;

const CheckoutMainSection = styled.div`
  display: grid;
  gap: 24px;

  /* Authorize.net payment block */
  #pay_block {
    .btn-bar {
      display: grid;
      gap: 24px;

      .cursive {
        position: relative;
        display: grid;
        grid-template-columns: auto auto;
        align-items: start;
        gap: 8px;
        place-self: end;

        picture.payment-system__icon {
          width: 150px;
          height: 35px;

          img {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: none;
            object-position: center;
          }
        }

        .arrow {
          position: absolute;
          right: -32px;
          top: 100%;
          transform: translateY(-16px);
        }
      }

      .btn {
        ${buttonBaseStyles}
        ${buttonVariantStyles.primary}
        ${buttonSizeStyles.l}
      }
    }
  }
`;

const PaymentOptionNote = styled.div`
  display: grid;
  place-items: start;
  gap: 1rem;
`;

const ProceedButton = styled(Button)`
  margin-top: 1rem;
`;

const DeliveryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap:4px;
`;

const Checkout = () => {
  const [payFullPriceEnabled, setPayFullPriceEnabled] = useState(false);
  const [inlineContent, setInlineContent] = useState(null);
  const [openPackageIds, setOpenPackageIds] = useState({});
  const togglePackageOpen = useCallback((id) => {
    setOpenPackageIds((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);
  const inlineContentRef = useRef(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isPluginEnabled } = usePluginUtils();
  const { data: config } = useConfig();
  const { invoice, setInvoice } = useInvoice();
  const callbackUrl = useMemo(
    () => buildCallbackUrl(getMembershipCallback() || {}, config?.react_widget_notification_url),
    [config]
  );
  // Popup relay flow - see usePaymentPopup.js. Primary strategy when framed; buildCallbackUrl's
  // notification-url fallback only applies if the popup can't be opened.
  const relayUrl = useMemo(() => buildRelayUrl(config), [config]);
  const relayOrigin = useMemo(() => buildRelayOrigin(config), [config]);
  const navigateToPaymentStatus = useNavigateToPaymentStatus();

  const recurringPayment = useRecurringInvoicePayment(invoice, {
    canOptIn: !!invoice.amount && !inlineContent,
  });
  const { isLoading: processorsLoading, data: paymentProcessors } = usePaymentProcessors();
  const { data: packagesData, isLoading: packagesLoading, hasNextPage, fetchNextPage, isFetchNextPageError } = usePackageInstances(invoice.id, {
    enabled: isPluginEnabled("package"),
  });
  const packages = packagesData?.pages?.flatMap(p => p.data) ?? [];

  useEffect(() => {
    if (hasNextPage) fetchNextPage();
  }, [hasNextPage, fetchNextPage]);

  const isWithDeposit = isPluginEnabled("deposit_paid_events") && invoice.is_with_deposit_amount;
  const paymentOptionIsSelectable = isWithDeposit && config.deposit_paid_events_is_client_can_pay_full_price;
  const disabledPayLater = invoice.rest_amount > 0 && invoice.deposit_is_client_can_pay_later === 0;
  const hasAvailablePackages = isPluginEnabled("package") && packages?.length > 0;
  const hasOnlyGiftCardsInInvoice = invoice.lines?.length > 0 && invoice.lines.every(line => line.type === CART_LINE_TYPES.PROMOTION);
  // Tips can only be applied to services (booking flow), never to cart items
  // like products/gift cards/memberships — so hide the block outside that flow.
  const activeFlow = useActiveFlow();
  const isBookingFlow = activeFlow === FlowType.BOOKING;

  const payment = usePaymentSelection(invoice, paymentProcessors, {
    disabledPayLater,
    hideDelay: invoice.payment_processor === "delay",
    recurringOnly: recurringPayment.isRecurring,
    recurringParams: recurringPayment.recurringParams,
  });
  const { selectedProcessor, filteredPaymentProcessors } = payment;
  const { handlePackageSelect, pendingPackageId } = usePackageSelection(invoice);

  const system = invoice.amount ? selectedProcessor : "free";

  const paymentPopup = usePaymentPopup(relayOrigin, () => {
    navigateToPaymentStatus(invoice.id, system);
  });

  const { mutate: payFullAmountMutate, isPending: isPayFullAmountPending } = useInvoicePayFullAmount(invoice.id);
  const { mutate: payDepositMutate, isPending: isPayDepositPending } = useInvoicePayDepositAmount(invoice.id);
  const { mutate: initPaymentQuery, isPending: initPaymentPending } = useInitPayment({
    invoice_id: invoice.id,
    system,
    callback_url: callbackUrl,
    ...recurringPayment.recurringParams,
    ...payment.storeParams,
  });
  const selectedProcessorImage = useAssetsSrc(`${PAYMENT_PROCESSORS_ICONS_PATH}${selectedProcessor}.png`);

  useEffect(() => {
    if (inlineContent && inlineContentRef.current) {
      const container = inlineContentRef.current;
      const scriptElements = container.querySelectorAll("script");

      scriptElements.forEach((oldScript) => {
        const newScript = document.createElement("script");

        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });

        newScript.innerHTML = oldScript.innerHTML;
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    }
  }, [inlineContent]);

  const initPayment = useCallback(() => {
    // When framed, open payment in a new top-level window - an in-place redirect can hit an
    // iframe sandbox that blocks the processor's top-navigation (e.g. Wix). Must be synchronous,
    // before the async call. Falls back to in-place navigation if the popup is blocked.
    const popup =
      isWidgetFramed() && relayUrl ? paymentPopup.open({ invoiceId: invoice.id, system }) : null;
    const usingPopup = !!popup;

    initPaymentQuery(
      { callback_url: usingPopup ? relayUrl : callbackUrl },
      {
        onSuccess: (data) => {
          clearMembershipCallback();
          if (data.redirect_url) {
            if (isSafeRedirectUrl(data.redirect_url)) {
              if (usingPopup) {
                paymentPopup.navigate(data.redirect_url);
              } else {
                window.location.href = data.redirect_url;
              }
            } else {
              if (usingPopup) paymentPopup.close();
              toast({
                title: "Error!",
                description: TOAST_ERROR_MESSAGE.DEFAULT,
                variant: "error",
              });
            }
          } else if (data.inline_content) {
            if (usingPopup) paymentPopup.close();
            setInlineContent(data.inline_content);
          } else if (usingPopup) {
            // Nothing to redirect to and no inline content (e.g. a free/zero-amount invoice) -
            // no popup needed after all.
            paymentPopup.close();
          }
        },
        onError: (error) => {
          if (usingPopup) paymentPopup.close();
          toast({
            title: "Error!",
            description: extractErrorMessage(error, TOAST_ERROR_MESSAGE.DEFAULT),
            variant: "error",
          });
        },
      }
    );
  }, [
    initPaymentQuery,
    invoice.id,
    system,
    setInlineContent,
    callbackUrl,
    relayUrl,
    paymentPopup.open,
    paymentPopup.navigate,
    paymentPopup.close,
    toast,
  ]);

  const { selectedMethodId, payWithSavedMethod } = payment;
  const handleProceed = useCallback(() => {
    if (!selectedMethodId) {
      initPayment();
      return;
    }

    payWithSavedMethod((paidInvoice) => {
      clearMembershipCallback();
      navigate(getInvoiceStatusRoute(invoice, activeFlow), {
        state: { invoiceId: paidInvoice.id },
      });
    });
  }, [selectedMethodId, payWithSavedMethod, initPayment, navigate, invoice, activeFlow]);

  const handlePayFullPrice = useCallback(() => {
    payFullAmountMutate(
      {},
      {
        onSuccess: (data) => {
          setPayFullPriceEnabled(true);
          setInvoice(data);
        },
        onError: (error) => {
          toast({
            title: "Error!",
            description: extractErrorMessage(error, TOAST_ERROR_MESSAGE.DEFAULT),
            variant: "error",
          });
        },
      }
    );
  }, [payFullAmountMutate, setInvoice, toast]);

  const handlePayDeposit = useCallback(() => {
    payDepositMutate(
      {},
      {
        onSuccess: (data) => {
          setPayFullPriceEnabled(false);
          setInvoice(data);
        },
        onError: (error) => {
          toast({
            title: "Error!",
            description: extractErrorMessage(error, TOAST_ERROR_MESSAGE.DEFAULT),
            variant: "error",
          });
        },
      }
    );
  }, [payDepositMutate, setInvoice, toast]);

  if (processorsLoading || packagesLoading || (hasNextPage && !isFetchNextPageError) || payment.isLoadingSavedMethods) {
    return (
      <Container>
        <StyledTitle size="h2">Checkout</StyledTitle>
        <LoadingIndicator page />
      </Container>
    );
  }

  return (
    <Container>
      <StyledTitle size="h2">Checkout</StyledTitle>
      <CheckoutMainSection $selectedProcessorImage={selectedProcessorImage}>
        {paymentOptionIsSelectable && !payFullPriceEnabled && (
          <PaymentOptionNote>
            <Note text="deposit_paid_events_pay_full_amount_info" />
            <Button
              variant="primary"
              title="deposit_paid_events_pay_full_amount_btn"
              onClick={handlePayFullPrice}
              loading={isPayFullAmountPending}
            />
          </PaymentOptionNote>
        )}
        {paymentOptionIsSelectable && payFullPriceEnabled && (
          <PaymentOptionNote>
            <Note text="deposit_paid_events_pay_full_amount_info" />
            <Button
              variant="primary"
              title="deposit_paid_events_pay_deposit_amount_btn"
              onClick={handlePayDeposit}
              loading={isPayDepositPending}
              disabled={invoice.package_instances?.length > 0}
            />
          </PaymentOptionNote>
        )}
        {(!isWithDeposit || payFullPriceEnabled) && hasAvailablePackages && !hasOnlyGiftCardsInInvoice && (
          <PackageInstanceSection>
            {packages.map((item) => (
              <PackageInstanceItem
                key={item.id}
                item={item}
                onSelect={(id) => handlePackageSelect(id, setInvoice)}
                isSelected={invoice.package_instances.find((instance) => instance.id === item.id)}
                isLoading={pendingPackageId === item.id}
                isOpen={!!openPackageIds[item.id]}
                onToggle={() => togglePackageOpen(item.id)}
                invoiceLines={invoice.lines}
              />
            ))}
          </PackageInstanceSection>
        )}
        {invoice.lines?.length > 0 && invoice.lines.some(line => line.type === "product" && line.product?.type !== "attribute") && config?.product_delivery_text && (
          <DeliveryWrapper>
            <Title size="h3">Delivery</Title>
            <Text dangerouslySetInnerHTML={{ __html: sanitizeHtml(config.product_delivery_text) }} />
          </DeliveryWrapper>
        )}
        {isBookingFlow && isPluginEnabled("tips") && <TipSelection invoice={invoice} />}
        {isPluginEnabled("promotion") && !hasOnlyGiftCardsInInvoice && <PromoCodeInput disabled={!payFullPriceEnabled && isWithDeposit} />}
        <CheckoutSummary renderLineExtra={recurringPayment.renderLineExtra}>
          <RecurringChargesBlock invoice={invoice} optedIn={recurringPayment.autoRenew} />
        </CheckoutSummary>
        {inlineContent && <div ref={inlineContentRef} dangerouslySetInnerHTML={{ __html: inlineContent }} />}
        {!!invoice.amount && !inlineContent && payment.showSavedMethods && (
          <SavedPaymentMethodsList
            methods={payment.savedMethods}
            selectedId={payment.selectedMethodId}
            onSelect={payment.selectSavedMethod}
          />
        )}
        {!!invoice.amount && !inlineContent && (
          <Stack $gap="1rem">
            <PaymentProcessorsList title="Payment method" subtitle="Please click on payment method to select it">
              {filteredPaymentProcessors.map((processor) => (
                <PaymentProcessorItem
                  key={processor.id}
                  processor={processor}
                  selected={selectedProcessor === processor.id}
                  onClick={() => payment.selectProcessor(processor.id)}
                />
              ))}
            </PaymentProcessorsList>
            {payment.canSaveMethod && (
              <SavePaymentMethodOption checked={payment.saveMethod} onChange={payment.setSaveMethod} />
            )}
          </Stack>
        )}
      </CheckoutMainSection>
      {!inlineContent && (
        <ProceedButton
          variant="primary"
          disabled={(invoice.amount && !payment.hasSelection) || paymentPopup.isPending}
          onClick={handleProceed}
          loading={initPaymentPending || payment.isPaying || paymentPopup.isPending}
          title="Proceed"
          fullWidth
        />
      )}
    </Container>
  );
};

export default Checkout;
